import {
  ReactElement, useEffect, useMemo, useState,
} from 'react';
import { UnitsInfoContainer } from 'pages/NewOverview/components/Loading/styles';
import { LoadingUnitsInfo } from 'pages/NewOverview/components/Loading/UnitsInfo';
import { ThermalComfortUnitCard } from './Card';
import { useThermalComfortDataContext } from 'pages/NewOverview/contexts/ThermalComfortDataContext';
import { useHistory } from 'react-router-dom';
import { handleFilterStateRoute } from 'pages/NewOverview/helpers/filters';
import { getUserProfile } from 'helpers/userProfile';
import { useFilterContext } from 'pages/NewOverview/contexts/FilterContext';
import { ThermalComfortVariant } from './types';

interface UnitsInfoProps {
  hasThermalComfort: boolean;
  loading: boolean;
}

export function UnitsInfo({
  hasThermalComfort,
  loading,
}: Readonly<UnitsInfoProps>): ReactElement {
  const [profile] = useState(getUserProfile());
  const { selectedOptions, client } = useFilterContext();
  const history = useHistory();
  const {
    loading: loadingThermal, data, refetch, units,
  } = useThermalComfortDataContext();

  const generalLoading = useMemo(() => loading || (loadingThermal && hasThermalComfort), [hasThermalComfort, loading, loadingThermal]);

  function getUnitsIdsByVariant(variant: ThermalComfortVariant): string[] {
    return units
      .filter((unit) => unit.thermalComfort === variant)
      .map((unit) => unit.id.toString());
  }

  function canSeeDetailsByValue(value: number | undefined): boolean {
    return (value ?? 0) > 0;
  }

  function onClickDetails(variant: ThermalComfortVariant): void {
    const filter = handleFilterStateRoute(client, selectedOptions, profile);
    const unitIds = getUnitsIdsByVariant(variant);

    if (unitIds.length) {
      filter.units = unitIds;
    }

    history.push('/analise/unidades', filter);
  }

  useEffect(() => {
    if (hasThermalComfort) {
      refetch(Number(client), selectedOptions);
    }
  }, [
    selectedOptions.group?.units,
    selectedOptions.state,
    selectedOptions.status,
  ]);

  if (generalLoading) {
    return (
      <LoadingUnitsInfo />
    );
  }

  return (
    <UnitsInfoContainer data-testId="thermal-confort-units-info">
      <ThermalComfortUnitCard
        variant="comfortable"
        value={hasThermalComfort && !loading ? data?.comfortable : undefined}
        onClickDetails={() => onClickDetails('comfortable')}
        canSeeDetails={canSeeDetailsByValue(hasThermalComfort && !loading ? data?.comfortable : undefined)}
      />
      <ThermalComfortUnitCard
        variant="belowLimit"
        value={hasThermalComfort && !loading ? data?.belowLimit : undefined}
        onClickDetails={() => onClickDetails('belowLimit')}
        canSeeDetails={canSeeDetailsByValue(hasThermalComfort && !loading ? data?.belowLimit : undefined)}
      />
      <ThermalComfortUnitCard
        variant="aboveLimit"
        value={hasThermalComfort && !loading ? data?.aboveLimit : undefined}
        onClickDetails={() => onClickDetails('aboveLimit')}
        canSeeDetails={canSeeDetailsByValue(hasThermalComfort && !loading ? data?.aboveLimit : undefined)}
      />
      <ThermalComfortUnitCard
        variant="inoperative"
        value={hasThermalComfort && !loading ? data?.inoperative : undefined}
        onClickDetails={() => onClickDetails('inoperative')}
        canSeeDetails={canSeeDetailsByValue(hasThermalComfort && !loading ? data?.inoperative : undefined)}
      />
    </UnitsInfoContainer>
  );
}
